import React, { useCallback, useEffect, useState } from 'react';
import { ThemeCtx, ThemeMode, getTheme, themeCssVars, FONT_UI, Theme } from '../theme';
import { BrandLogo } from '../components/BrandLogo';
import integritetMd from '../legal/integritet.md?raw';
import villkorMd from '../legal/villkor.md?raw';

// ─────────────────────────────────────────────────────────────────
// AS1e: integritetspolicy (/integritet) och användarvillkor (/villkor).
// Publika sidor utan inloggning – deras URL:er anges i App Store Connect.
// Texterna ligger som Markdown i src/legal/ så att de kan ändras utan kod.
// ─────────────────────────────────────────────────────────────────

export type LegalDoc = 'integritet' | 'villkor';
export const LEGAL_PATHS: Record<LegalDoc, string> = { integritet: '/integritet', villkor: '/villkor' };
const TITLES: Record<LegalDoc, string> = { integritet: 'Integritetspolicy', villkor: 'Användarvillkor' };
const TEXTS: Record<LegalDoc, string> = { integritet: integritetMd, villkor: villkorMd };

/** Vilken juridisk sida en sökväg pekar på, annars null. */
export function legalDocForPath(pathname: string): LegalDoc | null {
  const m = /^\/(integritet|villkor)\/?$/.exec(pathname);
  return m ? (m[1] as LegalDoc) : null;
}

function inline(text: string, key: string, T: Theme): React.ReactNode[] {
  // **fet**, [länk](url)
  const out: React.ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0; let m: RegExpExecArray | null; let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) out.push(<strong key={`${key}-${i++}`}>{m[1]}</strong>);
    else {
      const href = /^(https?:|mailto:|\/)/.test(m[3]) ? m[3] : '#';
      out.push(<a key={`${key}-${i++}`} href={href} style={{ color: T.accentText }} {...(href.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}>{m[2]}</a>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Minimal Markdown: rubriker (#–###), stycken, punktlistor, citat (> utkastrutan), fet stil, länkar. */
export function renderLegalMarkdown(md: string, T: Theme): React.ReactNode {
  return md.replace(/\r/g, '').split(/\n{2,}/).map((block, bi) => {
    const lines = block.split('\n').filter(l => l.trim());
    if (!lines.length) return null;
    const h = /^(#{1,3})\s+(.*)$/.exec(lines[0]);
    if (h && lines.length === 1) {
      const level = h[1].length as 1 | 2 | 3;
      const size = { 1: 30, 2: 20, 3: 16 }[level];
      return React.createElement(`h${level}`, { key: bi, style: { fontSize: size, margin: level === 1 ? '20px 0 6px' : '30px 0 8px', color: T.text, fontWeight: 800, letterSpacing: level === 1 ? '-0.02em' : undefined } }, inline(h[2], `h${bi}`, T));
    }
    if (lines.every(l => l.startsWith('>'))) {
      return <div key={bi} role="note" style={{ background: T.accentSoft, borderLeft: `4px solid ${T.accentFill}`, borderRadius: 8, padding: '12px 16px', margin: '14px 0', color: T.text }}>{inline(lines.map(l => l.replace(/^>\s?/, '')).join(' '), `q${bi}`, T)}</div>;
    }
    if (lines.every(l => /^\s*[-*]\s+/.test(l))) {
      return <ul key={bi} style={{ paddingLeft: 22, margin: '8px 0' }}>{lines.map((l, i) => <li key={i} style={{ margin: '5px 0' }}>{inline(l.replace(/^\s*[-*]\s+/, ''), `${bi}-${i}`, T)}</li>)}</ul>;
    }
    return <p key={bi} style={{ margin: '10px 0' }}>{inline(lines.join(' '), `p${bi}`, T)}</p>;
  });
}

/** Publik sida med eget tema (sparat läge eller systemets), som inbjudningssidan. */
export function LegalPage({ doc }: { doc: LegalDoc }) {
  const [mode, setMode] = useState<ThemeMode>(() => (localStorage.getItem('qvenari_theme') as ThemeMode) || (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'));
  const toggle = useCallback(() => setMode(m => { const n = m === 'dark' ? 'light' : 'dark'; localStorage.setItem('qvenari_theme', n); return n; }), []);
  const T = getTheme(mode);
  useEffect(() => { document.title = `${TITLES[doc]} – Qvenari`; }, [doc]);
  const other: LegalDoc = doc === 'integritet' ? 'villkor' : 'integritet';
  const back = (e: React.MouseEvent) => {
    // Öppnad inifrån appen: tillbaka dit man var. Direktlänk: till startsidan.
    if (window.history.length > 1 && document.referrer.startsWith(window.location.origin)) { e.preventDefault(); window.history.back(); }
  };
  return (
    <ThemeCtx.Provider value={{ mode, toggle }}>
      <style>{`${themeCssVars(T)} * { box-sizing: border-box; } body { margin: 0; background: ${T.bg}; font-family: ${FONT_UI}; }`}</style>
      <main style={{ maxWidth: 760, margin: '0 auto', padding: 'calc(env(safe-area-inset-top, 0px) + 24px) 16px 64px', fontSize: 16, lineHeight: 1.65, color: T.textSub, fontFamily: FONT_UI }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <a href="/" onClick={back} style={{ fontSize: 14, fontWeight: 600, color: T.accentText, textDecoration: 'none' }}>← Tillbaka till Qvenari</a>
          <BrandLogo size={26} />
        </div>
        {renderLegalMarkdown(TEXTS[doc], T)}
        <hr style={{ border: 0, borderTop: `1px solid ${T.border}`, margin: '36px 0 16px' }} />
        <a href={LEGAL_PATHS[other]} style={{ fontSize: 14, fontWeight: 600, color: T.accentText }}>{TITLES[other]}</a>
      </main>
    </ThemeCtx.Provider>
  );
}
